import type { ReactNode } from "react";
import { useRef, useState, useEffect } from "react";

type TooltipPosition = "right" | "left" | "top" | "bottom";

interface TooltipTestProps {
    children: ReactNode;
    content: ReactNode;
    placement?: TooltipPosition;
    delay?: number;
    className?: string;
    tooltipClassName?: string;
}

export function TooltipComponent({ children, content, tooltipClassName, className, placement = "top", delay = 200 }: TooltipTestProps) {
    const [open, setOpen] = useState(false);
    const timeRef = useRef<number | null>(null);
    const [canHover, setCanHover] = useState(true);

    useEffect(() => {
        // Check if device supports hover
        const mediaQuery = window.matchMedia("(hover: hover)");
        const updateHoverCapability = () => {
            setCanHover(mediaQuery.matches);
        };

        // Initial check
        updateHoverCapability();

        // Listen for changes
        mediaQuery.addEventListener("change", updateHoverCapability);

        // Cleanup
        return () => mediaQuery.removeEventListener("change", updateHoverCapability);
    }, []);

    const handleMouseEnter = () => {
        if (!canHover) return;
        timeRef.current = window.setTimeout(() => {
            setOpen(true);
        }, delay);
    };

    const handleMouseLeave = () => {
        if (!canHover) return;
        if (timeRef.current) {
            clearTimeout(timeRef.current);
            timeRef.current = null;
        }
        setOpen(false);
    };

    return (
        <div
            className={`relative inline-block ${className || ""}`}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {children}

            {open && canHover && (
                <span
                    className={`
                        absolute
                        z-[999]
                        max-w-[300px]
                        rounded-lg
                        bg-[#111]
                        px-[10px] py-[8px]
                        whitespace-normal
                        pointer-events-none
                        transition-opacity
                        duration-150
                        ease-in-out
                        group-hover:opacity-100

                        !w-[300px]
                        !text-white
                        !font-['DM_Sans',sans-serif]
                        !transition-[background,color] duration-[400ms] ease-[cubic-bezier(1,0,0.4,1)]
                        !p-[10px] !rounded-[10px]
 
                        !bg-[color-mix(in_srgb,#313030f8_97%,transparent)]
                        ${placement === "right" && "top-1/2 left-[105%] -translate-y-1/2"}
                        ${placement === "left" && "top-1/2 right-[105%] -translate-y-1/2"}
                        ${placement === "top" && "bottom-[110%] left-1/2 -translate-x-1/2"}
                        ${placement === "bottom" && "top-[110%] left-1/2 -translate-x-1/2"}
                        ${tooltipClassName}
                    `}
                >
                    {content}
                </span>
            )}
        </div>
    );
}